import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { AuthGuard } from './core/auth/auth.guard';
import { WorkitemLinkGuard } from './core/guards/workitemlink.guard';
import { ErrorComponent } from './shared/components/error/error.component';
import { UserUnauthorizeComponent } from './shared/components/user-unauthorize/user-unauthorize.component';
import { FeatureNames } from './shared/constants/feature-names';
import { TenantGuard } from '@ao/core/guards/tenant.gaurd';
import { environment } from '@ao/environments/environment';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/login/login.component').then(c => c.LoginComponent),
    title: 'Login',
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./pages/dashboard/dashboard.component').then(
        c => c.DashboardComponent
      ),
    canActivate: [MsalGuard, TenantGuard],
    data: { breadcrumb: 'Dashboard' },
    title: 'Dashboard',
    children: [
      {
        path: 'fs-caption-view',
        loadComponent: () =>
          import('./pages/fs-caption-view/fs-caption-view.component').then(
            c => c.FsCaptionViewComponent
          ),
        data: {
          breadcrumb: 'FS Caption View',
          featureNames: [FeatureNames.VIEW_FS_GRID],
        },
        canActivate: [MsalGuard, AuthGuard],
        title: 'FS Caption View',
        children: [
          {
            path: 'focus-area',
            loadComponent: () =>
              import('./pages/focus-area/focus-area.component').then(
                c => c.FocusAreaComponent
              ),
            canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
            data: { breadcrumb: 'Focus Area' },
            title: 'Focus Area',
          },
          {
            path: 'work-detail',
            loadComponent: () =>
              import('./pages/work-detail/work-detail.component').then(
                c => c.WorkDetailComponent
              ),
            canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
            data: { breadcrumb: 'Work Detail' },
            title: 'Work Detail',
          },
          {
            path: 'task-detail',
            loadComponent: () =>
              import('./pages/task-detail/task-detail.component').then(
                c => c.TaskDetailComponent
              ),
            canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
            data: { breadcrumb: 'Task Detail' },
            title: 'Task Detail',
          },
        ],
      },
    ],
  },
  // Flattened routes for direct access
  {
    path: 'focus-area',
    loadComponent: () =>
      import('./pages/focus-area/focus-area.component').then(
        c => c.FocusAreaComponent
      ),
    canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
    data: { breadcrumb: 'Focus Area' },
    title: 'Focus Area',
  },
  {
    path: 'work-detail',
    loadComponent: () =>
      import('./pages/work-detail/work-detail.component').then(
        c => c.WorkDetailComponent
      ),
    canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
    data: { breadcrumb: 'Work Detail' },
    title: 'Work Detail',
  },
  {
    path: 'task-detail',
    loadComponent: () =>
      import('./pages/task-detail/task-detail.component').then(
        c => c.TaskDetailComponent
      ),
    canActivate: [MsalGuard, WorkitemLinkGuard, AuthGuard],
    data: { breadcrumb: 'Task Detail' },
    title: 'Task Detail',
  },
  {
    path: 'client-management',
    loadComponent: () =>
      import(
        './pages/client-management-view/client-management-view.component'
      ).then(c => c.ClientManagementViewComponent),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'Client Management' },
    title: 'Client Management',
    children: [
      {
        path: 'entity-management',
        loadComponent: () =>
          import(
            './pages/entity-management-view/entity-management-view.component'
          ).then(c => c.EntityManagementViewComponent),
        canActivate: [MsalGuard, AuthGuard],
        data: { breadcrumb: 'Entity Management' },
        title: 'Entity Management',
      },
    ],
  },
  {
    path: 'user-management',
    loadComponent: () =>
      import(
        './pages/user-management-view/user-management-view.component'
      ).then(c => c.UserManagementViewComponent),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'User Management' },
    title: 'User Management',
  },
  {
    path: 'role-management',
    loadComponent: () =>
      import(
        './pages/role-management/role-management.component'
      ).then(c => c.RoleManagementComponent),
    canActivate: [MsalGuard, AuthGuard],
    data: {
      breadcrumb: 'Role Management',
      featureNames: [FeatureNames.ADD_ROLE],
    },
    title: 'Role Management',
  },
  {
    path: 'project-management',
    loadComponent: () =>
      import('./pages/project-management-view/project-management-view.component').then(
        c => c.ProjectManagementViewComponent
      ),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'Project Management' },
    title: 'Project Management',
    children: [
      {
        path: 'project-details',
        loadComponent: () =>
          import('./pages/project-management-details/project-management-details.component').then(
            c => c.ProjectManagementDetailsComponent
          ),
        canActivate: [MsalGuard, AuthGuard],
        data: { breadcrumb: 'Project Details' },
        title: 'Project Details',
      },
      {
        path: 'project-archival',
        loadComponent: () =>
          import('./components/archival-management/archival-deletion-stepper/archival-deletion-stepper.component').then(
            c => c.ArchivalDeletionStepperComponent
          ),
        canActivate: [MsalGuard, AuthGuard],
        data: { breadcrumb: 'Project Archival' },
        title: 'Project Archival',
      }
    ],
  },
  {
    path: 'escalations',
    loadComponent: () =>
      import('./pages/escalations-view/escalations-view.component').then(
        c => c.EscalationsViewComponent
      ),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'Escalations' },
    title: 'Escalation Management',
  },
  {
    path: 'privacy-notice',
    canMatch: [() => environment.production],
    loadComponent: () =>
      import('./pages/privacy-notice/privacy-notice.component').then(
        m => m.PrivacyNoticeComponent
      ),
    title: 'Privacy Notice',
  },
  {
    path: 'cookie-policies',
    canMatch: [() => environment.production],
    loadComponent: () =>
      import('./pages/cookie-policies/cookie-policies.component').then(
        m => m.CookiePoliciesComponent
      ),
    title: 'Cookie Policies',
  },
  {
    path: 'my-tasks',
    loadComponent: () =>
      import('./pages/task-list-view/task-list-view.component').then(
        c => c.TaskListViewComponent
      ),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'My Tasks' },
    title: 'My Tasks',
  },
  {
    path: 'help',
    loadComponent: () =>
      import('./pages/help-view/help-view.component').then(
        c => c.HelpViewComponent
      ),
    canActivate: [MsalGuard, AuthGuard],
    data: { breadcrumb: 'Help' },
    title: 'Help',
  },
  { path: 'error', component: ErrorComponent, title: 'Error' },
  {
    path: 'no-auth',
    component: UserUnauthorizeComponent,
    title: 'Unauthorized',
  },
  {
    path: '**',
    redirectTo: 'error',
  },
];
